/**
 * Document Helper Utilities
 *
 * Type-safe helpers for working with Prismic documents across different page types.
 * Centralizes common logic for extracting layout props, meta props, and type guards.
 */

import type {GroupField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {
  AiHubPageDocument,
  ArticleDocument,
  BlogPostDocument,
  CustomerStoryDocument,
  EventDocument,
  GlossaryPageDocument,
  GlossaryTermDocument,
  PageDocument,
  PageDocumentDataBreadcrumbsItem,
  Simplify,
} from 'prismicio-types'

/**
 * A resolved ancestor page's uid per language, used to build hreflang/footer
 * alternate-language links.
 */
export type ParentUid = {lang: string; uid: string | null}

/**
 * Union type for all renderable page documents
 */
export type RenderableDocument =
  | PageDocument
  | ArticleDocument
  | CustomerStoryDocument
  | BlogPostDocument
  | GlossaryPageDocument
  | GlossaryTermDocument
  | EventDocument
  | AiHubPageDocument

/**
 * Type guard: Check if document is a PageDocument
 */
export function isPageDocument(doc: RenderableDocument): doc is PageDocument {
  return doc.type === 'page'
}

/**
 * Type guard: Check if document is a BlogPostDocument
 */
export function isBlogPostDocument(doc: RenderableDocument): doc is BlogPostDocument {
  return doc.type === 'blog_post'
}

/**
 * Type guard: Check if document is an ArticleDocument
 */
export function isArticleDocument(doc: RenderableDocument): doc is ArticleDocument {
  return doc.type === 'article'
}

/**
 * Type guard: Check if document is a CustomerStoryDocument
 */
export function isCustomerStoryDocument(doc: RenderableDocument): doc is CustomerStoryDocument {
  return doc.type === 'customer_story'
}

/**
 * Extract breadcrumbs from any document type with proper type handling
 */
export function getBreadcrumbs(
  doc: RenderableDocument,
): GroupField<Simplify<PageDocumentDataBreadcrumbsItem>> | undefined {
  if (!('breadcrumbs' in doc.data)) {
    return undefined
  }

  const breadcrumbs = doc.data.breadcrumbs

  // Check if it's an array (GroupField) and not a ContentRelationshipField
  if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    return breadcrumbs as GroupField<Simplify<PageDocumentDataBreadcrumbsItem>>
  }

  return undefined
}

/**
 * Get alternate languages with parent UIDs
 */
export function getAlternateLanguagesWithParent<T extends RenderableDocument>(
  alternateLanguages: T['alternate_languages'],
  parentUids?: ParentUid[],
) {
  if (!parentUids) {
    return alternateLanguages
  }

  return alternateLanguages.map((altLang) => ({
    ...altLang,
    parentUid: parentUids.find((parentUid) => parentUid.lang === altLang.lang)?.uid ?? undefined,
  }))
}

/**
 * Extract meta props from any document type
 */
export function getMetaProps(doc: RenderableDocument) {
  const props: {
    description?: string
    title?: string
    imageUrl?: string
  } = {}

  if (isFilled.keyText(doc.data.meta_description)) {
    props.description = doc.data.meta_description
  }

  if (isFilled.keyText(doc.data.meta_title)) {
    props.title = doc.data.meta_title
  }

  if (isFilled.image(doc.data.meta_image)) {
    props.imageUrl = doc.data.meta_image.url
  }

  return props
}

/**
 * Safely get a field from PageDocument data
 * Returns undefined if document is not a PageDocument or field doesn't exist
 */
export function getPageField<K extends keyof PageDocument['data']>(
  doc: RenderableDocument,
  field: K,
): PageDocument['data'][K] | undefined {
  if (!isPageDocument(doc)) {
    return undefined
  }

  return doc.data[field]
}
